import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Input, Checkbox, Button, Spin, Empty } from 'antd'
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons'
import { useDebounce } from 'Hooks'
import './CategoryTreeSelector.scss'

interface CategoryItem {
  id?: string
  name?: string
  code?: string
  children?: CategoryItem[]
}

interface CategoryTreeSelectorProps {
  onSelect: (categories: string[]) => void
  selectedCategories?: string[]
  categories: CategoryItem[]
  loading: boolean
  hasMore: boolean
  onLoadMore: () => void
  onSearch: (keyword: string) => void
  operator?: string
}

// Extract common utility function to avoid code duplication
const getAllCategoryCodesFromItem = (item: CategoryItem): string[] => {
  const codes: string[] = []
  // Only add category codes, not product codes
  if (item.code && item.code.startsWith('CATEGORY_')) {
    codes.push(item.code)
  }
  if (item.children) {
    item.children.forEach((child) => {
      codes.push(...getAllCategoryCodesFromItem(child))
    })
  }
  return codes
}

const CategoryTreeSelector: React.FC<CategoryTreeSelectorProps> = ({
  onSelect,
  selectedCategories = [],
  categories = [],
  loading,
  hasMore,
  onLoadMore,
  onSearch,
  operator,
}) => {
  const [searchText, setSearchText] = useState('')
  const debouncedSearchText = useDebounce(searchText, 600)
  const [selectedItems, setSelectedItems] = useState<string[]>(selectedCategories)
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])

  useEffect(() => {
    setSelectedItems(selectedCategories)
  }, [selectedCategories])

  useEffect(() => {
    onSearch(debouncedSearchText)
  }, [debouncedSearchText, onSearch])

  const getChildCodes = (code: string): string[] => {
    const getAllChildCodes = (item: CategoryItem): string[] => {
      const codes: string[] = []
      if (item.children) {
        item.children.forEach((child) => {
          if (child.code && child.code.startsWith('CATEGORY_')) {
            codes.push(child.code)
          }
          codes.push(...getAllChildCodes(child))
        })
      }
      return codes
    }

    const findItem = (items: CategoryItem[]): CategoryItem | null => {
      for (const item of items) {
        if (item.code === code) return item
        if (item.children) {
          const found = findItem(item.children)
          if (found) return found
        }
      }
      return null
    }

    const item = findItem(categories)
    return item ? getAllChildCodes(item) : []
  }

  const handleSelectItem = (code: string) => {
    // For EQUAL or NOT_EQUAL operators, always select the clicked item (no toggle)
    if (operator === 'EQUAL' || operator === 'NOT_EQUAL') {
      const childCodes = getChildCodes(code)
      // Always select this item + all its children, replacing any previous selection
      setSelectedItems([code, ...childCodes])
    } else {
      // For IN_THE_FOLLOWING or NOT_IN_THE_FOLLOWING, toggle selection
      const isCurrentlySelected = selectedItems.includes(code)

      if (!isCurrentlySelected) {
        // Select this item and all its children
        const childCodes = getChildCodes(code)
        const newItems = [code, ...childCodes]
        const uniqueItems = [...new Set([...selectedItems, ...newItems])]
        setSelectedItems(uniqueItems)
      } else {
        // Uncheck this item and all its children
        const childCodes = getChildCodes(code)
        setSelectedItems(
          selectedItems.filter((item) => item !== code && !childCodes.includes(item))
        )
      }
    }
  }

  const handleSelectAllInCategory = useCallback(
    (category: CategoryItem, checked: boolean) => {
      // For EQUAL or NOT_EQUAL, always select when clicked (no toggle)
      if (operator === 'EQUAL' || operator === 'NOT_EQUAL') {
        handleSelectItem(category.code!)
      } else {
        // For IN_THE_FOLLOWING or NOT_IN_THE_FOLLOWING, allow selecting all children
        const allCodes = getAllCategoryCodesFromItem(category)

        if (checked) {
          setSelectedItems((prev) => [...new Set([...prev, ...allCodes])])
        } else {
          setSelectedItems((prev) => prev.filter((item) => !allCodes.includes(item)))
        }
      }
    },
    [operator]
  )

  // Memoize category selection calculations to avoid repeated computations
  const categorySelectionStates = useMemo(() => {
    const states = new Map<
      string,
      { allCodes: string[]; selectedCount: number; isPartially: boolean; isFully: boolean }
    >()

    const processCategory = (category: CategoryItem) => {
      const allCodes = getAllCategoryCodesFromItem(category)
      const selectedCount = allCodes.filter((code) => selectedItems.includes(code)).length
      const isPartially = selectedCount > 0 && selectedCount < allCodes.length
      const isFully = allCodes.length > 0 && selectedCount === allCodes.length

      if (category.code) {
        states.set(category.code, { allCodes, selectedCount, isPartially, isFully })
      }
    }

    const processCategories = (items: CategoryItem[]) => {
      items.forEach((item) => {
        processCategory(item)
        if (item.children) {
          processCategories(item.children)
        }
      })
    }

    processCategories(categories)
    return states
  }, [categories, selectedItems])

  const isCategoryPartiallySelected = useCallback(
    (category: CategoryItem): boolean => {
      const state = categorySelectionStates.get(category.code || '')
      return state?.isPartially || false
    },
    [categorySelectionStates]
  )

  const isCategoryFullySelected = useCallback(
    (category: CategoryItem): boolean => {
      const state = categorySelectionStates.get(category.code || '')
      return state?.isFully || false
    },
    [categorySelectionStates]
  )

  const handleConfirm = () => {
    onSelect(selectedItems)
  }

  const handleSearch = (value: string) => {
    setSearchText(value)
  }

  const renderTreeNode = (item: CategoryItem, level: number = 0) => {
    const isProduct = item.code?.startsWith('PRODUCT_')

    // Skip product items entirely
    if (isProduct) {
      return null
    }

    // Filter out product children
    const categoryChildren =
      item.children?.filter((child) => !child.code?.startsWith('PRODUCT_')) || []

    const hasChildren = categoryChildren.length > 0

    if (hasChildren || item.code?.startsWith('CATEGORY_')) {
      const isExpanded = expandedKeys.includes(item.code || item.id || '')
      const isPartiallySelected = isCategoryPartiallySelected(item)
      const isFullySelected = isCategoryFullySelected(item)

      return (
        <div key={item.code || item.id} className={`tree-category level-${level}`}>
          <div
            className="category-header"
            onClick={() => {
              // For EQUAL/NOT_EQUAL, clicking anywhere on the row selects this category
              if (operator === 'EQUAL' || operator === 'NOT_EQUAL') {
                handleSelectItem(item.code!)
              }
            }}
          >
            {hasChildren ? (
              <CaretRightOutlined
                className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  const key = item.code || item.id || ''
                  if (isExpanded) {
                    setExpandedKeys(expandedKeys.filter((k) => k !== key))
                  } else {
                    setExpandedKeys([...expandedKeys, key])
                  }
                }}
              />
            ) : (
              <span className="expand-icon-placeholder" />
            )}
            <Checkbox
              checked={isFullySelected}
              indeterminate={isPartiallySelected}
              onChange={(e) => {
                e.stopPropagation()
                handleSelectAllInCategory(item, e.target.checked)
              }}
              onClick={(e) => e.stopPropagation()}
              className="category-checkbox"
            />
            <span className="category-name">{item.name}</span>
          </div>
          {isExpanded && (
            <div className="category-children">
              {categoryChildren.map((child) => renderTreeNode(child, level + 1))}
            </div>
          )}
        </div>
      )
    }

    return null
  }

  return (
    <div className="category-tree-selector">
      <div className="selector-header">
        <Input
          placeholder="Search..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="selector-content">
        {loading && categories.length === 0 ? (
          <div className="loading-container">
            <Spin />
          </div>
        ) : categories.length === 0 ? (
          <Empty description="No categories found" />
        ) : (
          <>
            <div className="tree-container">
              {categories.map((category) => renderTreeNode(category, 0))}
            </div>

            {hasMore && (
              <div className="load-more-container">
                <Button onClick={onLoadMore} loading={loading}>
                  Load More
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedItems.length > 0 && (
        <div className="selector-footer">
          <Button type="link" onClick={() => setSelectedItems([])}>
            Clear All
          </Button>
          <Button type="primary" onClick={handleConfirm}>
            OK ({selectedItems.length} selected)
          </Button>
        </div>
      )}
    </div>
  )
}

export default CategoryTreeSelector
